import { merge } from 'es-toolkit';
import MarkdownToJSXComponent from 'markdown-to-jsx';
import type { MarkdownToJSX } from 'markdown-to-jsx';

import { Link } from '../../routing/routing';
import { MarkdownImage, MarkdownVideo } from './overrides';

const specialCharsToUnderscore = (text: string) => {
	return text
		.replace(/[^\p{Letter}\p{Mark}0-9]+/gu, ' ')
		.trim()
		.replace(/\s+/g, '_');
};

export const defaultOptions: MarkdownToJSX.Options = {
	overrides: {
		a: {
			component: Link,
			props: {
				className: 'text-takeda-dark-red-600 hover:text-takeda-dark-red-800',
			},
		},
		h1: {
			props: {
				className: `my-6 first:mt-0 font-bold text-heading-l`,
			},
		},
		h2: {
			props: {
				className: `my-6 first:mt-0 font-bold text-[calc(var(--text-heading-l)/1.25)]`,
			},
		},
		h3: {
			props: {
				className: `my-6 first:mt-0 font-bold text-[calc(var(--text-heading-l)/1.5)]`,
			},
		},
		h4: {
			props: {
				className: `my-6 first:mt-0 font-bold text-[calc(var(--text-heading-l)/1.75)]`,
			},
		},
		h5: {
			props: {
				className: `my-6 first:mt-0 font-bold text-[calc(var(--text-heading-l)/2)]`,
			},
		},
		h6: {
			props: {
				className: `my-6 first:mt-0 font-bold text-[calc(var(--text-heading-l)/2.5)]`,
			},
		},
		p: {
			props: {
				className: 'my-4 first:mt-0 last:mb-0',
			},
		},
		ul: {
			props: {
				className: 'my-6 first:mt-0 pl-10 list-disc [&_li::marker]:text-takeda-dark-red-600',
			},
		},
		ol: {
			props: {
				className: 'my-6 first:mt-0 pl-10 list-decimal',
			},
		},
		li: {
			props: {
				className: 'leading-7 [&_p:first-child]:m-0 [&>ul]:m-0 [&>ol]:m-0',
			},
		},
		b: {
			props: {
				className: 'font-bold',
			},
		},
		strong: {
			props: {
				className: 'font-bold',
			},
		},
		em: {
			props: {
				className: 'italic',
			},
		},
		blockquote: {
			props: {
				className: 'border-l-4 border-current my-10 first:mt-0 pl-6',
			},
		},
		pre: {
			props: {
				className:
					'border border-current overflow-x-auto leading-normal text-base my-6 first:mt-0 p-5',
			},
		},
		hr: {
			props: {
				className: 'bg-current border-0 h-px first:mt-0',
			},
		},
		figure: {
			props: {
				className: 'my-6 first:mt-0',
			},
		},
		figcaption: {
			props: {
				className: 'leading-normal text-base mt-1',
			},
		},
		table: {
			props: {
				className: 'border border-current border-collapse text-left w-full my-6 first:mt-0',
			},
		},
		caption: {
			props: {
				className: 'text-left text-base ml-1',
			},
		},
		iframe: {
			props: {
				className: 'w-full h-auto aspect-video',
			},
		},
		video: {
			component: MarkdownVideo,
		},
		img: {
			component: MarkdownImage,
		},
	},
};

export interface MarkdownProps {
	className?: string;
	children?: string;
	options?: MarkdownToJSX.Options;
	['data-sb-field-path']?: string;
	customId?: string;
}

const trimPattern = '(&#xA;|\\\\n|\\\\r)+';
const trimStartRegExp = new RegExp(`^${trimPattern}`, 'gm');
const trimEndRegExp = new RegExp(`${trimPattern}$`, 'gm');

export const sanitizeMarkdown = (string?: string) => {
	return (
		string
			?.trim()
			.replace(trimStartRegExp, '')
			.replace(trimEndRegExp, '')
			.replace(/&#xA;/g, '<br>')
			// A bug in markdown-to-jsx causes whitespace between tags collapse,
			// e.g., "<mark>Foo</mark> <mark>Bar</mark>" -> "<mark>Foo</mark><mark>Bar</mark>"
			// See https://onetakeda.atlassian.net/browse/MKT-4174
			// See https://github.com/quantizor/markdown-to-jsx/issues/398
			// TODO: fix spaces properly
			// .replace(/ /gm, '&#32;')
			// Fix list markers: restore regular space after list markers so markdown parser recognizes lists
			.replace(/^([*\-+]) +/gm, '$1 ') // unordered lists: *, -, +
			.replace(/^(\d+\.) +/gm, '$1 ') // ordered lists: 1., 2., etc.
	);
};

export const Markdown = ({
	className,
	children,
	options = {},
	customId,
	...props
}: MarkdownProps) => {
	const preparedMarkdown = sanitizeMarkdown(children) || '';

	const merge1 = merge({}, defaultOptions);
	const merge2 = merge(
		{
			// TODO: fix spaces properly
			// namedCodesToUnicode: {
			// 	'#32': '\u0020',
			// },
			slugify: (text: string) => {
				return customId
					? `${specialCharsToUnderscore(text)}-${customId}`
					: specialCharsToUnderscore(text);
			},
		},
		options,
	);
	const markdownOptions = merge(merge1, merge2);

	if (!children) {
		return null;
	}

	return (
		// A wrapper `div` is needed here to apply `className` to the whole thing.
		// If we apply `className` to `MarkdownToJSXComponent` instead, it leads to a bug where
		// the first Markdown element has `className`, which overrides CSS classes in defaultOptions
		// See also: https://onetakeda.atlassian.net/browse/MKT-4319
		<div className={className}>
			<MarkdownToJSXComponent data-testid="root" options={markdownOptions} {...props}>
				{preparedMarkdown}
			</MarkdownToJSXComponent>
		</div>
	);
};

export default Markdown;
